import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { globalErrorHandler } from '../error/saga';
import {
    buzzbridgeLoyaltyAdminWithdrawalUrl,
    payoutAdminExecuteUrl,
    payoutAdminRequestsUrl,
    payoutBanksUrl,
    payoutCountrySupportUrl,
    payoutStripeConfigUrl,
    payoutSupportedCountriesUrl,
    payoutVerifyAccountUrl,
    requestUrl,
} from '../../configurations/api/url';
import { showToastMessage } from '../../utils/AppUtils';
import { SentryCapture } from '../../analytics/Sentry';
import {
    getAdminPayoutRequests,
    getAdminPayoutRequestsFail,
    getAdminPayoutRequestsSuccess,
    getCountrySupport,
    getCountrySupportFail,
    getCountrySupportSuccess,
    getPayoutBanks,
    getPayoutBanksFail,
    getPayoutBanksSuccess,
    getStripeConfig,
    getStripeConfigFail,
    getStripeConfigSuccess,
    getSupportedCountries,
    getSupportedCountriesFail,
    getSupportedCountriesSuccess,
    resolveAdminPayout,
    resolveAdminPayoutFail,
    resolveAdminPayoutSuccess,
    executeAdminPayout,
    executeAdminPayoutFail,
    executeAdminPayoutSuccess,
    verifyPayoutAccount,
    verifyPayoutAccountFail,
    verifyPayoutAccountSuccess,
    type IGetPayoutBanksParams,
} from './actions';
import type {
    IAdminPayoutRequest,
    IExecuteAdminPayoutArgs,
    IGetAdminPayoutRequestsParams,
    IResolveAdminPayoutArgs,
} from '../../types/global/payout.types';

function normalizeAdminPayoutItem(raw: Record<string, unknown>): IAdminPayoutRequest {
    const legacyProduct = raw.product as string | undefined;
    const platform =
        (raw.platform as IAdminPayoutRequest['platform']) ?? (legacyProduct === 'meeteor' ? 'meeteor' : 'thspian');
    const source_type = String(raw.source_type ?? raw.request_kind ?? '');
    return { ...(raw as unknown as IAdminPayoutRequest), platform, source_type };
}

function buildAdminPayoutQuery(params: IGetAdminPayoutRequestsParams): string {
    const q = new URLSearchParams();
    q.set('platform', params.platform);
    if (params.kind && params.kind !== 'all') q.set('kind', params.kind);
    if (params.status) q.set('status', params.status);
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    return `?${q.toString()}`;
}

function buildPayoutBanksQuery(params: IGetPayoutBanksParams): string {
    const q = new URLSearchParams();
    if (params.currency) q.set('currency', params.currency);
    if (params.type) q.set('type', params.type);
    if (params.country) q.set('country', params.country);
    if (params.enabled_for_verification) q.set('enabled_for_verification', 'true');
    return q.toString();
}

function* getSupportedCountriesSaga(): Generator<any, any, any> {
    try {
        const response = yield call(api, payoutSupportedCountriesUrl, httpRequest.GET, null);
        yield put(getSupportedCountriesSuccess(response.data?.data ?? response.data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getSupportedCountriesFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getCountrySupportSaga({ payload }: { payload: string }): Generator<any, any, any> {
    try {
        const response = yield call(api, payoutCountrySupportUrl(payload), httpRequest.GET, null);
        yield put(getCountrySupportSuccess(response.data?.data ?? response.data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCountrySupportFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getPayoutBanksSaga({ payload }: { payload: IGetPayoutBanksParams }): Generator<any, any, any> {
    const cacheKey = payload.cacheKey ?? buildPayoutBanksQuery(payload);
    try {
        const response = yield call(api, payoutBanksUrl(buildPayoutBanksQuery(payload)), httpRequest.GET, null);
        const banks = response.data?.data?.banks ?? response.data?.banks ?? [];
        // Keep full list in store — UI filters by env / admin test-mode toggle.
        yield put(getPayoutBanksSuccess({ banks, cacheKey }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getPayoutBanksFail(error));
    }
}

function* getStripeConfigSaga({ payload }: { payload: string }): Generator<any, any, any> {
    try {
        const response = yield call(api, payoutStripeConfigUrl(payload), httpRequest.GET, null);
        yield put(getStripeConfigSuccess(response.data?.data ?? response.data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getStripeConfigFail(error));
    }
}

function* verifyPayoutAccountSaga({ payload }: { payload: any }): Generator<any, any, any> {
    try {
        const response = yield call(api, payoutVerifyAccountUrl, httpRequest.POST, payload, 0, 0);
        const result = response.data?.data ?? response.data;
        yield put(verifyPayoutAccountSuccess(result));
        if (result?.verified_account_name) {
            yield call(showToastMessage, `Verified: ${result.verified_account_name}`, 'success');
        } else {
            yield call(showToastMessage, 'Account verified successfully', 'success');
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(verifyPayoutAccountFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getAdminPayoutRequestsSaga({
    payload,
}: {
    payload: IGetAdminPayoutRequestsParams;
}): Generator<any, any, any> {
    try {
        const response = yield call(
            api,
            `${payoutAdminRequestsUrl}${buildAdminPayoutQuery(payload)}`,
            httpRequest.GET,
            null,
        );
        const data = response.data?.data ?? response.data;
        const items = (data?.items ?? []).map((row: Record<string, unknown>) => normalizeAdminPayoutItem(row));
        yield put(getAdminPayoutRequestsSuccess({ ...data, items }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getAdminPayoutRequestsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* resolveAdminPayoutSaga(action: {
    payload: IResolveAdminPayoutArgs;
    resolve?: (v: unknown) => void;
    reject?: (e: unknown) => void;
}): Generator<any, any, any> {
    const { payload, resolve, reject } = action;
    try {
        if (payload.platform === 'thspian' && payload.source_type === 'rewards_cash') {
            const status = payload.action === 'reject' ? 'rejected' : 'paid';
            yield call(
                api,
                buzzbridgeLoyaltyAdminWithdrawalUrl(payload.requestId),
                httpRequest.PATCH,
                { status, admin_note: payload.admin_note },
                0,
                0,
            );
        } else if (payload.source_type === 'loyalty_points') {
            yield call(
                api,
                buzzbridgeLoyaltyAdminWithdrawalUrl(payload.requestId),
                httpRequest.PATCH,
                {
                    status: payload.action === 'reject' ? 'failed' : 'fulfilled',
                    admin_note: payload.admin_note,
                },
                0,
                0,
            );
        } else {
            const request_status = payload.action === 'reject' ? 'denied' : 'approved';
            yield call(
                api,
                `${requestUrl}/${payload.requestId}`,
                httpRequest.PATCH,
                {
                    request_status,
                    resolution_message: payload.resolution_message ?? payload.admin_note ?? '',
                },
                0,
                0,
            );
        }
        yield put(resolveAdminPayoutSuccess(payload.requestId));
        yield call(showToastMessage, 'Payout request updated', 'success');
        if (resolve) resolve(payload.requestId);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(resolveAdminPayoutFail(error));
        if (reject) reject(error);
        yield call(globalErrorHandler, error);
    }
}

function* executeAdminPayoutSaga(action: {
    payload: IExecuteAdminPayoutArgs;
    resolve?: (v: unknown) => void;
    reject?: (e: unknown) => void;
}): Generator<any, any, any> {
    const { payload, resolve, reject } = action;
    try {
        const body: Record<string, unknown> = {
            platform: payload.platform,
            source_type: payload.source_type,
            id: payload.id,
        };
        if (payload.amount_minor != null) body.amount_minor = payload.amount_minor;
        if (payload.reason?.trim()) body.reason = payload.reason.trim();

        const response = yield call(api, payoutAdminExecuteUrl, httpRequest.POST, body, 0, 0);
        const result = response.data?.data ?? response.data;
        yield put(executeAdminPayoutSuccess(result));
        const ref = result?.transfer?.transfer_code ?? result?.transfer?.reference;
        yield call(showToastMessage, ref ? `Transfer initiated (${ref})` : 'Payout transfer initiated', 'success');
        if (resolve) resolve(result);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(executeAdminPayoutFail({ requestId: payload.id, error }));
        if (reject) reject(error);
        yield call(globalErrorHandler, error);
    }
}

function* supportedCountriesWatcher() {
    yield takeLatest(getSupportedCountries, getSupportedCountriesSaga);
}
function* countrySupportWatcher() {
    yield takeLatest(getCountrySupport, getCountrySupportSaga);
}
function* payoutBanksWatcher() {
    yield takeLatest(getPayoutBanks, getPayoutBanksSaga);
}
function* stripeConfigWatcher() {
    yield takeLatest(getStripeConfig, getStripeConfigSaga);
}
function* verifyAccountWatcher() {
    yield takeLatest(verifyPayoutAccount, verifyPayoutAccountSaga);
}
function* adminPayoutRequestsWatcher() {
    yield takeLatest(getAdminPayoutRequests, getAdminPayoutRequestsSaga);
}
function* resolveAdminPayoutWatcher() {
    yield takeLatest(resolveAdminPayout, resolveAdminPayoutSaga);
}
function* executeAdminPayoutWatcher() {
    yield takeLatest(executeAdminPayout, executeAdminPayoutSaga);
}

export default function* payoutSaga() {
    yield all([
        supportedCountriesWatcher(),
        countrySupportWatcher(),
        payoutBanksWatcher(),
        stripeConfigWatcher(),
        verifyAccountWatcher(),
        adminPayoutRequestsWatcher(),
        resolveAdminPayoutWatcher(),
        executeAdminPayoutWatcher(),
    ]);
}
